/**
 * Shared helpers for backend-authored validation rules.
 *
 * Rules are read exactly as stored in an attribute's
 * `configuration_data.validation_rules` - `FieldMetaSerializer` serves them
 * verbatim, so `validation_type` is lower-case and the bound lives under a key
 * named after it.
 *
 * Two consumers build schemas from the same rules (`buildZodSchema` in
 * `schema.ts`, `applyCustomAttributeRules` in `generated-helpers.ts`); the
 * pattern semantics live here so they cannot drift apart.
 */

import type { ValidationRule } from './types'

/** Mirrors Python's `re.escape` so a wildcard pattern keeps backend semantics. */
function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

/** Backend `pattern` rules are `*`/`?` wildcards, not regex (`_wildcard_match`). */
function wildcardToRegexSource(pattern: string): string {
  return escapeRegExp(pattern).replace(/\\\*/g, '.*').replace(/\\\?/g, '.')
}

/**
 * The anchored RegExp a `regex` or `pattern` rule tests, or `null` when the
 * rule carries neither (or an unparseable pattern - it is still enforced
 * server-side, so the form must not break).
 *
 * The server matches with `re.fullmatch`, while Zod's `.regex()` and
 * `RegExp.test` are substring tests - without the anchors, `AB[0-9]{4}` would
 * accept `xxAB1234` in the browser and then 400 on submit. Inner `^`/`$` stay
 * valid under the wrapper (no `m` flag), so alternations are safe.
 */
export function ruleRegExp(rule: ValidationRule): RegExp | null {
  let source: string
  if (rule.validation_type === 'regex') {
    source = rule.regex
  } else if (rule.validation_type === 'pattern') {
    source = wildcardToRegexSource(rule.pattern)
  } else {
    return null
  }
  try {
    return new RegExp(`^(?:${source})$`)
  } catch {
    return null
  }
}

/** A serial splits into everything up to its trailing digit run, plus that run. */
const SERIAL_PARTS = /^(.*?)(\d+)$/

function splitSerial(value: string): [string, string] | null {
  const match = SERIAL_PARTS.exec(value.trim())
  return match ? [match[1], match[2]] : null
}

function inSerialRange(value: string, bounds: { from: string; to: string }): boolean {
  const low = splitSerial(String(bounds?.from ?? ''))
  const high = splitSerial(String(bounds?.to ?? ''))
  const got = splitSerial(value)
  if (!low || !high || !got) return false
  const [lowPrefix, lowDigits] = low
  const [highPrefix, highDigits] = high
  const [gotPrefix, gotDigits] = got
  if (
    lowPrefix.toLowerCase() !== highPrefix.toLowerCase() ||
    lowPrefix.toLowerCase() !== gotPrefix.toLowerCase()
  ) {
    return false
  }
  // Same width first, or `SGW0600000` reads as 600000 and slips into a
  // 600000-699999 block. Equal-width digit strings then compare
  // lexicographically exactly as they would numerically, which also sidesteps
  // JS number precision on long runs.
  if (lowDigits.length !== highDigits.length || lowDigits.length !== gotDigits.length) {
    return false
  }
  return lowDigits <= gotDigits && gotDigits <= highDigits
}

/**
 * Whether a value falls inside ANY issued block. Mirrors
 * `_matches_serial_ranges` in `common/dynamic_attributes/validation.py`.
 *
 * Blocks are OR'd: a tenant issues codes in batches and a code belongs to one
 * of them. (Rules are AND'd, so a rule per block would reject every code that
 * was not in every block.)
 */
export function matchesSerialRanges(
  value: string | string[],
  ranges: { from: string; to: string }[],
): boolean {
  if (!Array.isArray(ranges)) return false
  const codes = Array.isArray(value) ? value : [value]
  if (codes.length === 0) return false
  return codes.every((code) => ranges.some((bounds) => inSerialRange(code, bounds)))
}

const MAX_RANGE_CODES = 2000

/**
 * Expand a From / To pair into the codes in between, inclusive.
 * Returns an error string when the pair could never match.
 */
export function expandSerialRange(
  from: string,
  to: string,
): { codes: string[] } | { error: string } {
  const start = from.trim()
  const end = to.trim() === '' ? start : to.trim()
  const low = splitSerial(start)
  const high = splitSerial(end)
  if (!low) {
    return { error: 'Must end in digits, e.g. L01-000001.' }
  }
  if (!high) {
    return { error: 'Must end in digits, e.g. L01-000200.' }
  }
  if (low[0].toLowerCase() !== high[0].toLowerCase()) {
    return { error: `Both codes must start with the same letters (${low[0]} and ${high[0]}).` }
  }
  if (low[1].length !== high[1].length) {
    return { error: 'Both codes must have the same number of digits.' }
  }
  if (low[1] > high[1]) {
    return { error: 'The first code must come before the last.' }
  }
  const first = Number(low[1])
  const last = Number(high[1])
  if (last - first + 1 > MAX_RANGE_CODES) {
    return { error: `A range can add at most ${MAX_RANGE_CODES} codes at once.` }
  }
  const width = low[1].length
  const codes: string[] = []
  for (let n = first; n <= last; n += 1) {
    codes.push(`${low[0]}${String(n).padStart(width, '0')}`)
  }
  return { codes }
}
